import { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import { LinearProgress, Chip, Box, Card, CardContent, Collapse, Alert } from '@mui/material';
import { Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import { WEBSOCKET_URL } from "../../../config";
import RinexServiceApi from './service';
import GnssControllerApi from '../gnssControllerApi';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import ReplayCircleFilledIcon from '@mui/icons-material/ReplayCircleFilled';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import StorageIcon from '@mui/icons-material/Storage';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import MemoryIcon from '@mui/icons-material/Memory';
import DatasetIcon from '@mui/icons-material/Dataset';
import TerminalIcon from '@mui/icons-material/Terminal';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BytesDisplay from '../../../components/BytesDisplay';

function fancyTimeFormat(duration) {
  // For long-running services (years), show days instead of just hours
  const days = ~~(duration / 86400);
  const hrs = ~~((duration % 86400) / 3600);
  const mins = ~~((duration % 3600) / 60);
  const secs = ~~duration % 60;

  let ret = "";

  if (days > 0) {
    ret += days + "d " + hrs + "h " + mins + "m";
  } else if (hrs > 0) {
    ret += hrs + ":" + (mins < 10 ? "0" : "") + mins + ":" + (secs < 10 ? "0" : "") + secs;
  } else {
    ret += mins + ":" + (secs < 10 ? "0" : "") + secs;
  }

  return ret;
}

const rinexservice = new RinexServiceApi();
const gnsscontroller = new GnssControllerApi();

export default function RINEXServiceStatusWidget(props) {
  let navigate = useNavigate();
  const id = props.id;
  const StatusWebSocket = useRef(null);

  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState();
  const [status, setStatus] = useState({});

  const [oldBytes, setOldBytes] = useState(0);
  const [increment, setIncrement] = useState(0);

  // Logs state
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  function fetchData() {
    setLoading(true);
    rinexservice.get(id).then(data => {
      setSettings(data);
      setLoading(false);
    });
  }

  const fetchLogs = () => {
    setLogsLoading(true);
    setLogsError(null);
    gnsscontroller.logs(100)
      .then(data => {
        if (data.success) {
          setLogs(data.logs);
        } else {
          setLogsError(data.error || 'Failed to fetch logs');
        }
        setLogsLoading(false);
      })
      .catch(error => {
        setLogsError(error.message || 'Failed to fetch logs');
        setLogsLoading(false);
      });
  };

  const toggleLogs = () => {
    if (!showLogs) {
      fetchLogs();
    }
    setShowLogs(!showLogs);
  };

  // Calculate hourly progress based on current time
  const getHourlyProgress = () => {
    const now = new Date();
    const minutes = now.getMinutes();
    const seconds = now.getSeconds();
    return ((minutes * 60 + seconds) / 3600) * 100;
  };

  useEffect(() => {
    // Calculate data rate increment
    if(status.hasOwnProperty('bytes') && status.bytes) {
      if(oldBytes === 0) {
        setIncrement(0);
      }
      else {
        setIncrement((Number(status.bytes) - Number(oldBytes)) / 100);
      }
      setOldBytes(status.bytes);
    }
  }, [status.bytes]);

  useEffect(() => {
    let reconnectTimeout;

    const connectStatusWebSocket = () => {
      StatusWebSocket.current = new WebSocket(
        WEBSOCKET_URL + '/ws/gnss/' + id + '/');

      StatusWebSocket.current.onclose = function (e) {
        reconnectTimeout = setTimeout(() => {
          connectStatusWebSocket();
        }, 1000);
      };

      StatusWebSocket.current.onerror = function (error) {
        // WebSocket error - connection will close and trigger reconnect
      };

      StatusWebSocket.current.onmessage = function (e) {
        try {
          const data = JSON.parse(e.data);
          setStatus(data);
        }
        catch (error) {
          console.error('Error parsing status WebSocket data:', error);
        }
      };
    };

    connectStatusWebSocket();

    return () => {
      clearTimeout(reconnectTimeout);
      if (StatusWebSocket.current) {
        StatusWebSocket.current.close();
      }
    };
  }, [id]);

  if (loading) {
    return <LinearProgress />
  }

  const getStatusIcon = () => {
    if (status.status === 'starting') return <PendingIcon color="warning" />;
    if (status.running) return <CheckCircleIcon color="success" />;
    return <ErrorIcon color="error" />;
  };

  const getStatusChip = () => {
    if (status.status === 'starting') return <Chip label="Starting" color="warning" size="small" />;
    if (status.running) return <Chip label="Recording" color="success" size="small" />;
    return <Chip label="Stopped" color="error" size="small" />;
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      {/* Header Section */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {getStatusIcon()}
          <Box>
            <Typography variant="h6" component="div">
              {status.filename || settings.path}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              RINEX Data Log • Service ID: {id}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {getStatusChip()}
          <Button
            variant="contained"
            size="small"
            color={status.running ? "error" : "success"}
            startIcon={status.running ? <StopCircleIcon /> : <PlayCircleFilledIcon />}
            disabled={status.status === 'starting'}
            onClick={() => {
              if (status.running) {
                rinexservice.stop(settings.id);
              } else {
                rinexservice.start(settings.id);
              }
              setOldBytes(0);
            }}
          >
            {status.running ? 'Stop' : 'Start'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ReplayCircleFilledIcon />}
            disabled={status.status === 'starting'}
            onClick={() => rinexservice.sync(settings.id)}
          >
            Restart
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SettingsIcon />}
            onClick={() => navigate("/gnss/rinex/" + id + "/edit")}
          >
            Settings
          </Button>
        </Stack>
      </Stack>

      {/* Progress Bar */}
      <Box sx={{ mb: 2 }}>
        {status.status === 'starting' ? (
          <LinearProgress variant="indeterminate" color="warning" sx={{ height: 6, borderRadius: 1 }} />
        ) : status.running ? (
          <LinearProgress variant="determinate" value={getHourlyProgress()} color="success" sx={{ height: 6, borderRadius: 1 }} />
        ) : (
          <LinearProgress variant="determinate" value={1} color="error" sx={{ height: 6, borderRadius: 1 }} />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {status.running ? `Recording hourly file - ${Math.floor(getHourlyProgress())}% complete` : status.message}
        </Typography>
      </Box>

      {/* Key Metrics Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary">Runtime</Typography>
              <Typography variant="h6" color={status.running ? "success.main" : "error.main"} sx={{ mt: 0.5 }}>
                {fancyTimeFormat(status.run_time_secs || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary">Hourly Progress</Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>
                {Math.floor(getHourlyProgress())}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary">Bytes Recorded</Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>
                <BytesDisplay bytes={status.bytes || 0} />
              </Typography>
              {increment > 0 && (
                <Typography variant="caption" color="success.main">
                  +<BytesDisplay bytes={increment} />/s
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary">Current File</Typography>
              <Typography variant="body2" noWrap title={status.filename || "-"} sx={{ mt: 0.5 }}>
                {status.filename ? (status.filename.length > 20 ? '...' + status.filename.slice(-17) : status.filename) : "-"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Details Grid */}
      <Grid container spacing={2}>
        {/* Recording Settings */}
        <Grid item xs={12} md={6} lg={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <StorageIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2">Recording Settings</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="body2">Interval: {status.interval || "-"} seconds</Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  {status.const_gps && <Chip label="GPS" size="small" color="primary" />}
                  {status.const_glonass && <Chip label="GLONASS" size="small" color="success" />}
                  {status.const_galileo && <Chip label="Galileo" size="small" color="error" />}
                  {status.const_beidou && <Chip label="BeiDou" size="small" color="warning" />}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* RINEX Header Info */}
        <Grid item xs={12} md={6} lg={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <DatasetIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2">RINEX Header Info</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="body2">Marker Name: {status.header_marker_name || "-"}</Typography>
                <Typography variant="body2">Observer: {status.header_observer || "-"}</Typography>
                <Typography variant="body2">Agency: {status.header_agency || "-"}</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* GNSS Receiver Info */}
        <Grid item xs={12} md={6} lg={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <MemoryIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2">GNSS Receiver</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="body2">Port: {status.hasOwnProperty('gnss') ? status.gnss.path : "-"}</Typography>
                <Typography variant="body2">Baudrate: {status.hasOwnProperty('gnss') ? status.gnss.baudrate : "-"}</Typography>
                <Typography variant="body2">S/N: {status.hasOwnProperty('gnss') ? status.gnss.serialnumber : "-"}</Typography>
                <Typography variant="body2">Firmware: {status.hasOwnProperty('gnss') ? status.gnss.firmwareversion : "-"}</Typography>
                <Typography variant="body2">Hardware: {status.hasOwnProperty('gnss') ? status.gnss.hardwareversion : "-"}</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Antenna Info */}
        <Grid item xs={12} md={6} lg={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <GpsFixedIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2">Antenna Info</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="body2">Type: {status.antenna_type || "-"}</Typography>
                <Typography variant="body2">Radome: {status.antenna_radome || "-"}</Typography>
                <Typography variant="body2">North Offset: {status.antenna_north_offset !== undefined && status.antenna_north_offset !== null ? `${status.antenna_north_offset} m` : "-"}</Typography>
                <Typography variant="body2">East Offset: {status.antenna_east_offset !== undefined && status.antenna_east_offset !== null ? `${status.antenna_east_offset} m` : "-"}</Typography>
                <Typography variant="body2">Up Offset: {status.antenna_up_offset !== undefined && status.antenna_up_offset !== null ? `${status.antenna_up_offset} m` : "-"}</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Service Logs Section */}
      <Box sx={{ mt: 3 }}>
        <Card variant="outlined">
          <CardContent sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <TerminalIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2">GNSS Controller Service Logs</Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                {showLogs && (
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={fetchLogs}
                    disabled={logsLoading}
                  >
                    Refresh
                  </Button>
                )}
                <Button
                  size="small"
                  endIcon={showLogs ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={toggleLogs}
                >
                  {showLogs ? 'Hide' : 'Show'} Logs
                </Button>
              </Stack>
            </Stack>

            <Collapse in={showLogs}>
              <Box sx={{ mt: 2 }}>
                {logsLoading && <LinearProgress />}
                {logsError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {logsError}
                  </Alert>
                )}
                {!logsLoading && !logsError && logs.length > 0 && (
                  <Box
                    sx={{
                      backgroundColor: '#1e1e1e',
                      color: '#d4d4d4',
                      p: 2,
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      overflowX: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all'
                    }}
                  >
                    {logs.map((line, index) => (
                      <Box
                        key={index}
                        sx={{
                          mb: 0.5,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.05)'
                          }
                        }}
                      >
                        {line}
                      </Box>
                    ))}
                  </Box>
                )}
                {!logsLoading && !logsError && logs.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                    No logs available
                  </Typography>
                )}
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      </Box>
    </Paper>
  );
}